// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// render.ts — the floor plan, drawn.
//
// THE CANVAS IS SIZED FROM A MEASURED CONTAINER, never from a formula against screen height. The
// design specified `min(vw/8, availH/11)` against the DEVICE height, which on the reference phone
// produced 43px cells — under the 44px minimum — and pushed the commit bar clean off an iPhone SE.
// A real getBoundingClientRect on the board's own parent is the only number that knows what the
// browser chrome actually took, and a transient 0-size measurement is ignored rather than turned into
// a NaN scale that silently drops every tap.
//
// TWO VISUAL CHANNELS, KEPT SEPARATE. Floor LIGHTNESS is the only thing encoding light level, because
// light is the input to how far somebody can see. So an arc is an outline plus a SINGLE
// non-accumulating tint — a cell inside three arcs is tinted exactly once — and the route's own risk
// is drawn as a per-cell DOT rather than another tint, because a tint over a tint is indistinguishable
// on exactly the cells the preview exists to explain.

import { Band, CELLS, COLS, Floor, Mark, ROWS, Verb } from './tuning';
import { DoorState, chebyshev, hasLos, idx, xOf, yOf } from './map';
import {
  MState,
  band,
  isBright,
  lensFacing,
  threatMask,
  validExits,
  type State,
} from './rules';
import { FLOOR_RAMP, PALETTE } from './palette';

export interface View {
  /** The staged route's cells, in order, or empty. */
  route: number[];
  /** Per-route-cell risk: 0 clear, 1 exposed (projected), 2 seen (now). */
  routeRisk: number[];
  /** A cell to ring — the tutorial's hint, or the slug target being aimed. */
  hint: number;
  /** Cells legal to throw a slug at, while the slug button is armed. */
  slugCells: number[];
  /** The par route, drawn only on the summary. */
  par: number[];
  reduced: boolean;
}

export const emptyView = (): View => ({ route: [], routeRisk: [], hint: -1, slugCells: [], par: [], reduced: false });

export interface Geometry {
  cell: number;
  ox: number;
  oy: number;
  w: number;
  h: number;
}

/**
 * Fit an 8x10 board into whatever space the layout actually gave us.
 *
 * A 0-size measurement happens for real — a board in a hidden panel, a tab that was backgrounded
 * during layout — and computing a scale from it yields a cell size of 0 and world coordinates of NaN,
 * which silently drops every tap rather than failing loudly. Returning the previous geometry and
 * retrying next frame is the only safe answer.
 */
export function fit(wPx: number, hPx: number): Geometry | null {
  if (!(wPx > 8) || !(hPx > 8)) return null;
  const cell = Math.max(1, Math.floor(Math.min(wPx / COLS, hPx / ROWS)));
  const w = cell * COLS;
  const h = cell * ROWS;
  return { cell, ox: Math.floor((wPx - w) / 2), oy: Math.floor((hPx - h) / 2), w, h };
}

/** Which cell is under a client-space point, or -1. Never `offsetX` — it lies under DPR and zoom. */
export function cellAt(g: Geometry, rect: DOMRect, clientX: number, clientY: number): number {
  const x = Math.floor((clientX - rect.left - g.ox) / g.cell);
  const y = Math.floor((clientY - rect.top - g.oy) / g.cell);
  if (x < 0 || x >= COLS || y < 0 || y >= ROWS) return -1;
  return idx(x, y);
}

const withAlpha = (hex: string, a: number): string => {
  const h = hex.replace('#', '');
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

/**
 * Brownout only, and RENDER-ONLY: cells outside the intruder's knowledge radius that no torch is
 * lighting are not drawn. The rules never branch on this — tests/rules.test.ts hashes full state with
 * the fog on and off and asserts byte equality — because a presentation choice that becomes a rules
 * branch is how a mode stops being the same game.
 */
function known(s: State, c: number): boolean {
  const r = s.mode.awareness;
  if (r <= 0) return true;
  if (chebyshev(s.pos, c) <= r) return true;
  // A torch beam sweeping through a doorway two rooms away is exactly what you are meant to navigate by.
  return isBright(s, c) && hasLos(s.grid, s.doors, s.pos, c);
}

export function draw(ctx: CanvasRenderingContext2D, g: Geometry, s: State, v: View, shake = 0): void {
  const { cell, ox, oy } = g;
  ctx.save();
  ctx.clearRect(0, 0, g.w + ox * 2, g.h + oy * 2);
  ctx.fillStyle = PALETTE.void;
  ctx.fillRect(0, 0, g.w + ox * 2, g.h + oy * 2);
  ctx.translate(ox + shake, oy);

  const px = (c: number): number => xOf(c) * cell;
  const py = (c: number): number => yOf(c) * cell;
  const tier = band(s);
  const exits = validExits(s);

  // ── the floor, and the floor alone, carries light level ──────────────────
  for (let c = 0; c < CELLS; c++) {
    if (s.grid.wall[c]) continue;
    if (!known(s, c)) continue;
    const bright = isBright(s, c);
    const doorHere = s.grid.doorAt[c] >= 0;
    const level = doorHere ? 2 : bright ? 3 : 1;
    ctx.fillStyle = FLOOR_RAMP[level];
    ctx.fillRect(px(c), py(c), cell, cell);
    drawTexture(ctx, s.grid.floorT[c], px(c), py(c), cell);
  }

  // ── walls ────────────────────────────────────────────────────────────────
  //
  // Drawn as a RECESSIVE fill with a bright edge, not as bright blocks. Filling them solid in the
  // wall colour made the walls the loudest thing on screen and the floor read as the hole rather than
  // the room — the map inverted. The edge is what carries the >= 3:1 the contrast test holds walls to;
  // the fill just has to stay out of the way of the floor's lightness ramp, which is the one channel
  // encoding how far anybody can see.
  ctx.fillStyle = PALETTE.wallFill;
  for (let c = 0; c < CELLS; c++) {
    if (!s.grid.wall[c] || !known(s, c)) continue;
    ctx.fillRect(px(c), py(c), cell, cell);
  }
  ctx.strokeStyle = PALETTE.wall;
  ctx.lineWidth = Math.max(1.5, cell * 0.07);
  const half = ctx.lineWidth / 2;
  for (let c = 0; c < CELLS; c++) {
    if (!s.grid.wall[c] || !known(s, c)) continue;
    const x = xOf(c);
    const y = yOf(c);
    // Only the faces that actually border a room. An interior wall block draws nothing, so a thick
    // partition reads as one line rather than a stack of boxes.
    const open = (nx: number, ny: number): boolean =>
      nx >= 0 && nx < COLS && ny >= 0 && ny < ROWS && !s.grid.wall[idx(nx, ny)];
    ctx.beginPath();
    if (open(x, y + 1)) {
      ctx.moveTo(px(c), py(c) + cell - half);
      ctx.lineTo(px(c) + cell, py(c) + cell - half);
    }
    if (open(x, y - 1)) {
      ctx.moveTo(px(c), py(c) + half);
      ctx.lineTo(px(c) + cell, py(c) + half);
    }
    if (open(x + 1, y)) {
      ctx.moveTo(px(c) + cell - half, py(c));
      ctx.lineTo(px(c) + cell - half, py(c) + cell);
    }
    if (open(x - 1, y)) {
      ctx.moveTo(px(c) + half, py(c));
      ctx.lineTo(px(c) + half, py(c) + cell);
    }
    ctx.stroke();
  }

  // ── arcs: outline plus ONE non-accumulating fill ─────────────────────────
  const watched = threatMask(s);
  const soon = threatMask(s, s.posture, true);
  ctx.fillStyle = withAlpha(PALETTE.arc, 0.16);
  for (let c = 0; c < CELLS; c++) {
    if (!watched[c] || !known(s, c)) continue;
    ctx.fillRect(px(c), py(c), cell, cell);
  }
  // THE OUTLINE IS WHAT MAKES AN ARC A SHAPE. A sixteen-per-cent tint is all the floor's lightness ramp
  // can afford — that ramp is the only thing encoding light, which is the input to how far anybody can
  // see — so on its own the watched region reads as a slightly different grey and the player cannot
  // tell where it ENDS. Stroking only the boundary between watched and unwatched costs nothing and
  // draws the actual edge of somebody's attention.
  ctx.strokeStyle = withAlpha(PALETTE.arc, 0.75);
  ctx.lineWidth = Math.max(1.5, cell * 0.045);
  const edge = ctx.lineWidth / 2;
  const seen = (nx: number, ny: number): boolean =>
    nx >= 0 && nx < COLS && ny >= 0 && ny < ROWS && watched[idx(nx, ny)] === 1;
  for (let c = 0; c < CELLS; c++) {
    if (!watched[c] || !known(s, c)) continue;
    const x = xOf(c);
    const y = yOf(c);
    ctx.beginPath();
    if (!seen(x, y - 1)) {
      ctx.moveTo(px(c), py(c) + edge);
      ctx.lineTo(px(c) + cell, py(c) + edge);
    }
    if (!seen(x, y + 1)) {
      ctx.moveTo(px(c), py(c) + cell - edge);
      ctx.lineTo(px(c) + cell, py(c) + cell - edge);
    }
    if (!seen(x - 1, y)) {
      ctx.moveTo(px(c) + edge, py(c));
      ctx.lineTo(px(c) + edge, py(c) + cell);
    }
    if (!seen(x + 1, y)) {
      ctx.moveTo(px(c) + cell - edge, py(c));
      ctx.lineTo(px(c) + cell - edge, py(c) + cell);
    }
    ctx.stroke();
  }
  // The next-turn projection, dashed at CALM and RESTLESS where patrols really are deterministic, and
  // degraded to an undashed wash at SWEEP and above — previewability is a resource the building takes.
  ctx.strokeStyle = withAlpha(PALETTE.arcProjected, tier >= Band.Sweep ? 0.3 : 0.55);
  ctx.lineWidth = Math.max(1, cell * 0.05);
  ctx.setLineDash(tier >= Band.Sweep ? [] : [cell * 0.22, cell * 0.22]);
  for (let c = 0; c < CELLS; c++) {
    if (!soon[c] || watched[c] || !known(s, c)) continue;
    ctx.strokeRect(px(c) + 2, py(c) + 2, cell - 4, cell - 4);
  }
  ctx.setLineDash([]);

  // ── the furniture ────────────────────────────────────────────────────────
  for (let c = 0; c < CELLS; c++) {
    if (!known(s, c)) continue;
    const d = s.grid.doorAt[c];
    if (d >= 0) {
      const st = s.doors[d].state;
      ctx.fillStyle =
        st === DoorState.Open ? PALETTE.doorOpen : st === DoorState.Locked ? PALETTE.doorLocked : PALETTE.doorShut;
      const thin = Math.max(2, cell * 0.16);
      if (st === DoorState.Open) {
        ctx.fillRect(px(c) + cell * 0.1, py(c) + cell * 0.5 - thin / 2, cell * 0.18, thin);
        ctx.fillRect(px(c) + cell * 0.72, py(c) + cell * 0.5 - thin / 2, cell * 0.18, thin);
      } else {
        ctx.fillRect(px(c) + cell * 0.1, py(c) + cell * 0.5 - thin / 2, cell * 0.8, thin);
      }
    }
    if (s.grid.ductAt[c] >= 0) {
      ctx.strokeStyle = PALETTE.duct;
      ctx.lineWidth = Math.max(1.5, cell * 0.07);
      ctx.strokeRect(px(c) + cell * 0.22, py(c) + cell * 0.22, cell * 0.56, cell * 0.56);
      ctx.beginPath();
      ctx.moveTo(px(c) + cell * 0.22, py(c) + cell * 0.5);
      ctx.lineTo(px(c) + cell * 0.78, py(c) + cell * 0.5);
      ctx.stroke();
    }
  }
  for (const room of s.b.breakers.keys()) {
    const c = s.b.breakers[room];
    if (c < 0 || !known(s, c)) continue;
    ctx.fillStyle = PALETTE.breaker;
    ctx.globalAlpha = s.roomGloom[room] ? 0.45 : 1;
    ctx.fillRect(px(c) + cell * 0.36, py(c) + cell * 0.24, cell * 0.28, cell * 0.52);
    ctx.globalAlpha = 1;
  }
  for (const c of s.b.chutes) {
    if (!known(s, c)) continue;
    ctx.strokeStyle = PALETTE.chute;
    ctx.lineWidth = Math.max(1.5, cell * 0.08);
    ctx.beginPath();
    ctx.arc(px(c) + cell / 2, py(c) + cell / 2, cell * 0.3, 0, Math.PI * 2);
    ctx.stroke();
  }
  for (const c of exits) {
    if (!known(s, c)) continue;
    ctx.strokeStyle = PALETTE.exit;
    ctx.globalAlpha = 0.9;
    ctx.lineWidth = Math.max(1.5, cell * 0.07);
    ctx.strokeRect(px(c) + 3, py(c) + 3, cell - 6, cell - 6);
    ctx.globalAlpha = 1;
  }

  // ── marks: white, plus a DISTINCT GLYPH so kind survives without colour ──
  for (const m of s.marks) {
    if (!known(s, m.cell)) continue;
    ctx.strokeStyle = withAlpha(PALETTE.mark, 0.7);
    ctx.fillStyle = withAlpha(PALETTE.mark, 0.7);
    ctx.lineWidth = Math.max(1, cell * 0.06);
    drawMark(ctx, m.kind, m.dir, px(m.cell), py(m.cell), cell);
  }

  // ── loot ─────────────────────────────────────────────────────────────────
  for (const it of s.items) {
    if (it.taken || it.confiscated || it.cell < 0 || !known(s, it.cell)) continue;
    ctx.fillStyle = PALETTE.loot;
    ctx.fillRect(px(it.cell) + cell * 0.24, py(it.cell) + cell * 0.3, cell * 0.52, cell * 0.4);
  }

  // ── the staged route: a line, plus a DOT per cell for its risk ───────────
  if (v.route.length) {
    ctx.lineWidth = Math.max(2, cell * 0.1);
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(px(s.pos) + cell / 2, py(s.pos) + cell / 2);
    for (const c of v.route) ctx.lineTo(px(c) + cell / 2, py(c) + cell / 2);
    const worst = Math.max(0, ...v.routeRisk);
    ctx.strokeStyle = worst >= 2 ? PALETTE.routeSeen : worst >= 1 ? PALETTE.routeExposed : PALETTE.routeClear;
    ctx.stroke();
    for (let i = 0; i < v.route.length; i++) {
      const c = v.route[i];
      const r = v.routeRisk[i] ?? 0;
      // Hollow / half / filled. A DIFFERENT visual channel from the arc tint on purpose.
      ctx.strokeStyle = r >= 2 ? PALETTE.routeSeen : r >= 1 ? PALETTE.routeExposed : PALETTE.routeClear;
      ctx.fillStyle = ctx.strokeStyle;
      ctx.beginPath();
      ctx.arc(px(c) + cell / 2, py(c) + cell / 2, cell * 0.16, 0, Math.PI * 2);
      if (r >= 2) ctx.fill();
      else ctx.stroke();
      if (r === 1) {
        ctx.beginPath();
        ctx.arc(px(c) + cell / 2, py(c) + cell / 2, cell * 0.08, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  for (const c of v.slugCells) {
    ctx.strokeStyle = withAlpha(PALETTE.routeExposed, 0.8);
    ctx.lineWidth = Math.max(1, cell * 0.05);
    ctx.strokeRect(px(c) + cell * 0.2, py(c) + cell * 0.2, cell * 0.6, cell * 0.6);
  }

  if (v.par.length > 1) {
    ctx.strokeStyle = withAlpha(PALETTE.textDim, 0.65);
    ctx.lineWidth = Math.max(1.5, cell * 0.06);
    ctx.setLineDash([cell * 0.2, cell * 0.2]);
    ctx.beginPath();
    ctx.moveTo(px(v.par[0]) + cell / 2, py(v.par[0]) + cell / 2);
    for (const c of v.par.slice(1)) ctx.lineTo(px(c) + cell / 2, py(c) + cell / 2);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // ── the bodies ───────────────────────────────────────────────────────────
  for (const l of s.lenses) {
    if (!known(s, l.cell)) continue;
    drawEye(ctx, px(l.cell), py(l.cell), cell, PALETTE.lens, lensFacing(l, s.turn), true);
  }
  for (const m of s.minders) {
    if (!known(s, m.cell)) {
      drawBearing(ctx, s, m.cell, cell, px, py);
      continue;
    }
    const col =
      m.state === MState.Chase
        ? PALETTE.minderChasing
        : m.state === MState.Investigate
          ? PALETTE.minderLooking
          : PALETTE.minderCalm;
    drawEye(ctx, px(m.cell), py(m.cell), cell, col, m.facing, false);
    // WHAT HE IS DOING IS A SHAPE, NOT ONLY A COLOUR. The three states use Okabe-Ito hues, which are
    // chosen to stay apart under every common colour-vision deficiency — but blue and amber sit within
    // 1.03:1 of each other in LUMINANCE, so on a greyscale screen, or for a player who reads shape
    // faster than hue, "calm" and "coming to look at you" would be the same picture. A dashed ring for
    // investigating and a solid one for chasing says it without relying on colour at all.
    if (m.state !== MState.Patrol) {
      ctx.strokeStyle = col;
      ctx.lineWidth = Math.max(1.5, cell * 0.06);
      ctx.setLineDash(m.state === MState.Investigate ? [cell * 0.16, cell * 0.12] : []);
      ctx.beginPath();
      ctx.arc(px(m.cell) + cell / 2, py(m.cell) + cell / 2, cell * 0.42, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }
  if (s.custodian >= 0 && band(s) >= Band.Lockdown && known(s, s.custodian)) {
    ctx.fillStyle = PALETTE.custodian;
    ctx.beginPath();
    ctx.arc(px(s.custodian) + cell / 2, py(s.custodian) + cell / 2, cell * 0.3, 0, Math.PI * 2);
    ctx.fill();
  }

  // ── you ──────────────────────────────────────────────────────────────────
  if (!s.inDuct) {
    ctx.fillStyle = s.posture === Verb.Creep ? PALETTE.intruderCreep : PALETTE.intruder;
    const r = s.posture === Verb.Creep ? 0.22 : 0.3;
    ctx.beginPath();
    ctx.arc(px(s.pos) + cell / 2, py(s.pos) + cell / 2, cell * r, 0, Math.PI * 2);
    ctx.fill();
    if (s.posture === Verb.Bolt) {
      ctx.strokeStyle = PALETTE.intruder;
      ctx.lineWidth = Math.max(1.5, cell * 0.07);
      ctx.beginPath();
      ctx.arc(px(s.pos) + cell / 2, py(s.pos) + cell / 2, cell * 0.42, 0, Math.PI * 2);
      ctx.stroke();
    }
  } else {
    ctx.strokeStyle = PALETTE.intruder;
    ctx.lineWidth = Math.max(2, cell * 0.09);
    ctx.setLineDash([cell * 0.15, cell * 0.15]);
    ctx.beginPath();
    ctx.arc(px(s.pos) + cell / 2, py(s.pos) + cell / 2, cell * 0.34, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  if (v.hint >= 0) {
    // Dashed, and in the interface's own accent rather than any colour the board already uses for a
    // body or an exit — a hint that looks like a minder is a hint that teaches the wrong thing.
    ctx.strokeStyle = PALETTE.hint;
    ctx.lineWidth = Math.max(2, cell * 0.09);
    ctx.setLineDash([cell * 0.18, cell * 0.14]);
    ctx.strokeRect(px(v.hint) + 3, py(v.hint) + 3, cell - 6, cell - 6);
    ctx.setLineDash([]);
  }

  ctx.restore();
}

/** Floor type is TEXTURE, never hue, so the map you read for space is the map you read for volume. */
function drawTexture(ctx: CanvasRenderingContext2D, t: number, x: number, y: number, cell: number): void {
  if (t === Floor.Carpet) return;
  ctx.strokeStyle = 'rgba(255,255,255,0.09)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  if (t === Floor.Tile) {
    ctx.moveTo(x, y + 0.5);
    ctx.lineTo(x + cell, y + 0.5);
  } else {
    for (let i = 0; i < cell; i += Math.max(4, cell / 4)) {
      ctx.moveTo(x + i, y + cell);
      ctx.lineTo(x + i + cell, y);
    }
  }
  ctx.stroke();
}

function drawMark(ctx: CanvasRenderingContext2D, kind: Mark, dir: number, x: number, y: number, cell: number): void {
  const cx = x + cell / 2;
  const cy = y + cell / 2;
  const r = cell * 0.2;
  ctx.beginPath();
  switch (kind) {
    case Mark.Scuff: {
      // A chevron pointing the way you went. That is the whole information content of a scuff.
      const a = (dir * Math.PI) / 4;
      const dx = Math.cos(a) * r;
      const dy = -Math.sin(a) * r;
      ctx.moveTo(cx - dy - dx * 0.4, cy + dx - dy * 0.4);
      ctx.lineTo(cx + dx, cy + dy);
      ctx.lineTo(cx + dy - dx * 0.4, cy - dx - dy * 0.4);
      ctx.stroke();
      break;
    }
    case Mark.OpenDoor:
    case Mark.ForcedDoor:
      ctx.moveTo(cx - r, cy - r);
      ctx.lineTo(cx - r * 0.4, cy - r);
      ctx.moveTo(cx - r, cy - r);
      ctx.lineTo(cx - r, cy + r);
      ctx.moveTo(cx - r, cy + r);
      ctx.lineTo(cx - r * 0.4, cy + r);
      ctx.stroke();
      break;
    case Mark.GloomRoom:
      ctx.arc(cx, cy, r * 0.7, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx - r, cy + r);
      ctx.lineTo(cx + r, cy - r);
      ctx.stroke();
      break;
    case Mark.MissingCase:
      ctx.strokeRect(cx - r, cy - r * 0.75, r * 2, r * 1.5);
      break;
    case Mark.Slug:
      ctx.arc(cx, cy, r * 0.5, 0, Math.PI * 2);
      ctx.fill();
      break;
  }
}

/** A body: a disc with a wedge showing which way it is facing. */
function drawEye(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  cell: number,
  colour: string,
  facing: number,
  fixed: boolean,
): void {
  const cx = x + cell / 2;
  const cy = y + cell / 2;
  ctx.fillStyle = colour;
  ctx.beginPath();
  if (fixed) ctx.rect(cx - cell * 0.22, cy - cell * 0.22, cell * 0.44, cell * 0.44);
  else ctx.arc(cx, cy, cell * 0.28, 0, Math.PI * 2);
  ctx.fill();
  const a = (facing * Math.PI) / 4;
  ctx.strokeStyle = colour;
  ctx.lineWidth = Math.max(2, cell * 0.1);
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.lineTo(cx + Math.cos(a) * cell * 0.46, cy - Math.sin(a) * cell * 0.46);
  ctx.stroke();
}

/**
 * Brownout: a body you cannot see but have heard renders as a bearing arrow on the edge of what you
 * know, never as a figure. Knowing roughly where somebody is, and not exactly, is the mode.
 */
function drawBearing(
  ctx: CanvasRenderingContext2D,
  s: State,
  cell: number,
  size: number,
  px: (c: number) => number,
  py: (c: number) => number,
): void {
  if (s.mode.awareness <= 0) return;
  const dx = xOf(cell) - xOf(s.pos);
  const dy = yOf(cell) - yOf(s.pos);
  const len = Math.max(1, Math.abs(dx) + Math.abs(dy));
  const r = s.mode.awareness;
  const ex = xOf(s.pos) + Math.round((dx / len) * r);
  const ey = yOf(s.pos) + Math.round((dy / len) * r);
  if (ex < 0 || ex >= COLS || ey < 0 || ey >= ROWS) return;
  ctx.fillStyle = withAlpha(PALETTE.minderLooking, 0.5);
  const c = idx(ex, ey);
  ctx.beginPath();
  ctx.arc(px(c) + size / 2, py(c) + size / 2, size * 0.12, 0, Math.PI * 2);
  ctx.fill();
}
